import React from 'react';
import { XCircle, Plus } from 'lucide-react';
import { Button } from '../ui/button';

const RegisterDeviceModal = ({
  activeCategory,
  categories,
  registerForm,
  setRegisterForm,
  registerDevice,
  onClose,
  deviceTypeOptions,
  manufacturers,
}) => {
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b flex items-center justify-between">
          <h3 className="text-xl font-bold text-gray-900">
            Add {categories.find(c => c.id === activeCategory)?.name.slice(0, -1)}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XCircle className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={registerDevice} className="p-6 space-y-6">
          {/* Common Fields */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Device Name *</label>
              <input
                type="text"
                required
                value={registerForm.device_name}
                onChange={(e) => setRegisterForm({ ...registerForm, device_name: e.target.value })}
                placeholder={activeCategory === 'cameras' ? 'e.g., Front Gate Camera' :
                  activeCategory === 'smart_meters' ? 'e.g., Main Electricity Meter' : 'e.g., Fleet Vehicle 001'}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Device Type *</label>
              <select
                required
                value={registerForm.device_type}
                onChange={(e) => setRegisterForm({ ...registerForm, device_type: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
              >
                <option value="">Select type...</option>
                {deviceTypeOptions[activeCategory]?.map((type) => (
                  <option key={type.id} value={type.id}>{type.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Manufacturer *</label>
              <select
                required
                value={registerForm.manufacturer}
                onChange={(e) => setRegisterForm({ ...registerForm, manufacturer: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
              >
                <option value="">Select manufacturer...</option>
                {manufacturers[activeCategory]?.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
                <option value="Other">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Model *</label>
              <input
                type="text"
                required
                value={registerForm.model}
                onChange={(e) => setRegisterForm({ ...registerForm, model: e.target.value })}
                placeholder="e.g., Pro 4K"
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Location *</label>
            <input
              type="text"
              required
              value={registerForm.location}
              onChange={(e) => setRegisterForm({ ...registerForm, location: e.target.value })}
              placeholder="e.g., Building A, Floor 2, Room 201"
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
            />
          </div>

          {/* Camera-specific fields */}
          {activeCategory === 'cameras' && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">IP Address *</label>
                  <input
                    type="text"
                    required
                    value={registerForm.ip_address}
                    onChange={(e) => setRegisterForm({ ...registerForm, ip_address: e.target.value })}
                    placeholder="192.168.1.100"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">MAC Address</label>
                  <input
                    type="text"
                    value={registerForm.mac_address}
                    onChange={(e) => setRegisterForm({ ...registerForm, mac_address: e.target.value })}
                    placeholder="AA:BB:CC:DD:EE:FF"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Resolution</label>
                  <select
                    value={registerForm.resolution}
                    onChange={(e) => setRegisterForm({ ...registerForm, resolution: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                  >
                    <option value="720p">720p HD</option>
                    <option value="1080p">1080p Full HD</option>
                    <option value="2k">2K QHD</option>
                    <option value="4k">4K Ultra HD</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Stream Protocol</label>
                  <select
                    value={registerForm.stream_protocol}
                    onChange={(e) => setRegisterForm({ ...registerForm, stream_protocol: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                  >
                    <option value="rtsp">RTSP</option>
                    <option value="onvif">ONVIF</option>
                    <option value="http">HTTP</option>
                    <option value="rtmp">RTMP</option>
                  </select>
                </div>
              </div>
              <div className="flex gap-6">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={registerForm.has_night_vision}
                    onChange={(e) => setRegisterForm({ ...registerForm, has_night_vision: e.target.checked })}
                    className="rounded"
                  />
                  <span className="text-sm">Night Vision</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={registerForm.has_ptz}
                    onChange={(e) => setRegisterForm({ ...registerForm, has_ptz: e.target.checked })}
                    className="rounded"
                  />
                  <span className="text-sm">PTZ Control</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={registerForm.has_audio}
                    onChange={(e) => setRegisterForm({ ...registerForm, has_audio: e.target.checked })}
                    className="rounded"
                  />
                  <span className="text-sm">Audio</span>
                </label>
              </div>
            </>
          )}

          {/* Smart Meter-specific fields */}
          {activeCategory === 'smart_meters' && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Meter ID *</label>
                  <input
                    type="text"
                    required
                    value={registerForm.meter_id}
                    onChange={(e) => setRegisterForm({ ...registerForm, meter_id: e.target.value })}
                    placeholder="e.g., MTR-2024-001"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Communication Type</label>
                  <select
                    value={registerForm.communication_type}
                    onChange={(e) => setRegisterForm({ ...registerForm, communication_type: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                  >
                    <option value="nb_iot">NB-IoT</option>
                    <option value="lora">LoRa</option>
                    <option value="zigbee">Zigbee</option>
                    <option value="cellular">Cellular (4G/5G)</option>
                    <option value="wifi">WiFi</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Reading Interval (min)</label>
                  <select
                    value={registerForm.reading_interval_minutes}
                    onChange={(e) => setRegisterForm({ ...registerForm, reading_interval_minutes: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                  >
                    <option value={5}>Every 5 minutes</option>
                    <option value={15}>Every 15 minutes</option>
                    <option value={30}>Every 30 minutes</option>
                    <option value={60}>Every hour</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Utility Provider</label>
                  <input
                    type="text"
                    value={registerForm.utility_provider}
                    onChange={(e) => setRegisterForm({ ...registerForm, utility_provider: e.target.value })}
                    placeholder="e.g., BSES, Tata Power"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                  />
                </div>
              </div>
            </>
          )}

          {/* Dashcam-specific fields */}
          {activeCategory === 'dashcams' && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Vehicle ID *</label>
                  <input
                    type="text"
                    required
                    value={registerForm.vehicle_id}
                    onChange={(e) => setRegisterForm({ ...registerForm, vehicle_id: e.target.value })}
                    placeholder="e.g., VEH-001"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Vehicle Type</label>
                  <select
                    value={registerForm.vehicle_type}
                    onChange={(e) => setRegisterForm({ ...registerForm, vehicle_type: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                  >
                    <option value="car">Car</option>
                    <option value="truck">Truck</option>
                    <option value="bus">Bus</option>
                    <option value="taxi">Taxi</option>
                    <option value="motorcycle">Motorcycle</option>
                    <option value="van">Van</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">License Plate</label>
                  <input
                    type="text"
                    value={registerForm.license_plate}
                    onChange={(e) => setRegisterForm({ ...registerForm, license_plate: e.target.value })}
                    placeholder="e.g., DL 01 AB 1234"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Storage (GB)</label>
                  <select
                    value={registerForm.storage_gb}
                    onChange={(e) => setRegisterForm({ ...registerForm, storage_gb: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                  >
                    <option value={32}>32 GB</option>
                    <option value={64}>64 GB</option>
                    <option value={128}>128 GB</option>
                    <option value={256}>256 GB</option>
                    <option value={512}>512 GB</option>
                  </select>
                </div>
              </div>
              <div className="flex gap-6">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={registerForm.has_gps}
                    onChange={(e) => setRegisterForm({ ...registerForm, has_gps: e.target.checked })}
                    className="rounded"
                  />
                  <span className="text-sm">GPS Tracking</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={registerForm.has_cloud_backup}
                    onChange={(e) => setRegisterForm({ ...registerForm, has_cloud_backup: e.target.checked })}
                    className="rounded"
                  />
                  <span className="text-sm">Cloud Backup</span>
                </label>
              </div>
            </>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" className="bg-orange-600 hover:bg-orange-700">
              <Plus className="h-4 w-4 mr-2" />
              Register Device
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RegisterDeviceModal;
